import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { LayoutDashboard, Search, Kanban, Users, LogOut, Zap, Settings, Sliders, DollarSign, BarChart3, Key, UserPlus, Upload, Plug, BookOpen, Copy, Link2, Trophy } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import ChangePasswordModal from "../ChangePasswordModal";
import WorkspaceSwitcher from "../WorkspaceSwitcher";
import api from "../../lib/api";
import { toast } from "sonner";

const navItems = [
  { to: "/", icon: LayoutDashboard, label: "Command", testid: "nav-dashboard" },
  { to: "/scraper", icon: Search, label: "Scraper", testid: "nav-scraper" },
  { to: "/import", icon: Upload, label: "Import CSV", testid: "nav-import" },
  { to: "/pipeline", icon: Kanban, label: "Pipeline", testid: "nav-pipeline" },
  { to: "/leads", icon: Users, label: "Leads", testid: "nav-leads" },
  { to: "/analytics", icon: BarChart3, label: "Analytics", testid: "nav-analytics" },
  { to: "/ab-history", icon: Trophy, label: "A/B History", testid: "nav-ab-history" },
  { to: "/roi", icon: DollarSign, label: "ROI", testid: "nav-roi" },
  { to: "/campaign", icon: Settings, label: "Campaign", testid: "nav-campaign" },
  { to: "/team", icon: UserPlus, label: "Team", testid: "nav-team" },
  { to: "/mcp-access", icon: Plug, label: "MCP / API", testid: "nav-mcp" },
  { to: "/settings", icon: Sliders, label: "Settings", testid: "nav-settings" },
  { to: "/help", icon: BookOpen, label: "Guide", testid: "nav-help" },
];

export default function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [showChangePw, setShowChangePw] = useState(false);
  const [bookingUrl, setBookingUrl] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/settings");
        if (!cancelled) setBookingUrl(data?.personal_booking_url || "");
      } catch {
        /* silent — sidebar is non-critical UI */
      }
    })();
    return () => { cancelled = true; };
  }, [user?.id]);

  return (
    <div className="min-h-screen bg-white flex">
      {/* Sidebar */}
      <aside className="w-60 border-r border-zinc-200 flex flex-col bg-white" data-testid="sidebar">
        <div className="px-5 py-6 border-b border-zinc-200">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 bg-[#002FA7] flex items-center justify-center">
              <Zap className="w-4 h-4 text-white" strokeWidth={2.5} />
            </div>
            <div>
              <div className="font-heading font-black text-base leading-none tracking-tight">LEADFORGE</div>
              <div className="overline mt-1">AI · SDR · CRM</div>
            </div>
          </div>
        </div>

        <div className="px-3 py-3 border-b border-zinc-200">
          <WorkspaceSwitcher />
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1">
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/"}
              data-testid={item.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-[#002FA7] text-white"
                    : "text-zinc-700 hover:bg-zinc-100"
                }`
              }
            >
              <item.icon className="w-4 h-4" strokeWidth={2} />
              <span>{item.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="border-t border-zinc-200 p-4">
          {bookingUrl && (
            <div className="mb-3" data-testid="sidebar-booking-link-widget">
              <div className="text-[9px] font-bold uppercase tracking-wider text-zinc-400 mb-1.5 flex items-center gap-1">
                <Link2 className="w-2.5 h-2.5" /> My booking link
              </div>
              <div className="flex items-center gap-1">
                <code className="flex-1 px-2 py-1.5 bg-zinc-50 border border-zinc-200 text-[10px] font-mono text-zinc-700 truncate" title={bookingUrl}>
                  {bookingUrl.replace(/^https?:\/\//, "")}
                </code>
                <button
                  type="button"
                  onClick={() => { navigator.clipboard.writeText(bookingUrl); toast.success("Copied"); }}
                  data-testid="sidebar-copy-booking-link"
                  title="Copy"
                  className="px-2 py-1.5 border border-zinc-300 hover:bg-zinc-100"
                >
                  <Copy className="w-3 h-3 text-zinc-600" />
                </button>
              </div>
            </div>
          )}
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 bg-zinc-900 text-white flex items-center justify-center font-heading font-bold text-sm">
              {user?.name?.[0]?.toUpperCase() || "?"}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold truncate">{user?.name}</div>
              <div className="text-xs text-zinc-500 truncate font-mono">{user?.email}</div>
            </div>
          </div>
          <button
            data-testid="change-password-btn"
            onClick={() => setShowChangePw(true)}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 mb-2 text-xs font-semibold uppercase tracking-wider border border-zinc-300 hover:bg-zinc-100 transition-colors"
          >
            <Key className="w-3.5 h-3.5" />
            Change password
          </button>
          <button
            data-testid="logout-btn"
            onClick={() => { logout(); navigate("/login"); }}
            className="w-full flex items-center justify-center gap-2 px-3 py-2 text-xs font-semibold uppercase tracking-wider border border-zinc-300 hover:bg-zinc-900 hover:text-white hover:border-zinc-900 transition-colors"
          >
            <LogOut className="w-3.5 h-3.5" />
            Sign Out
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 overflow-x-hidden">
        <Outlet />
      </main>

      {/* Voluntary password change from sidebar button */}
      {showChangePw && (
        <ChangePasswordModal onClose={() => setShowChangePw(false)} />
      )}
    </div>
  );
}
